import type { JsonObject } from '../model/integration';

type ParseResult = { value: JsonObject; error: null } | { value: null; error: string };

export const isJsonObject = (value: unknown): value is JsonObject =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const parseJsonObject = (text: string): ParseResult => {
  try {
    const value: unknown = JSON.parse(text);
    return isJsonObject(value) ? { value, error: null } : { value: null, error: 'Debe ser un objeto JSON ({ … })' };
  } catch {
    return { value: null, error: 'JSON inválido' };
  }
};

export const formatJson = (value: unknown): string => JSON.stringify(value, null, 2);

export const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const getAtPath = (source: unknown, path: string[]): unknown =>
  path.reduce<unknown>((value, key) => (isJsonObject(value) ? value[key] : undefined), source);

const omitKey = (source: JsonObject, key: string): JsonObject =>
  Object.fromEntries(Object.entries(source).filter(([entryKey]) => entryKey !== key));

/** Sets a nested key, or removes it when value is undefined; objects left empty are removed too */
export const setAtPath = (source: JsonObject, path: string[], value: unknown): JsonObject => {
  const [key, ...rest] = path;
  if (key === undefined) return source;
  const next = rest.length === 0 ? value : setAtPath(isJsonObject(source[key]) ? source[key] : {}, rest, value);
  return next === undefined || (isJsonObject(next) && Object.keys(next).length === 0)
    ? omitKey(source, key)
    : { ...source, [key]: next };
};

const sortKeys = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(sortKeys)
    : isJsonObject(value)
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((key) => [key, sortKeys(value[key])])
        )
      : value;

// Postgres JSONB returns keys in its own order, so equality must not depend on it
export const isSameJson = (a: unknown, b: unknown): boolean =>
  JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b));
